import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, shareReplay, tap } from 'rxjs';
import { AwardFundingDetails } from '@k12/models';
import { DatFileSendHistory } from '../../models/dat-file-send-history.model';
import { AcademicYear, LotteryAwardSettings } from '../../models/program-settings.model';
import { ClassWalletFile } from '../../models/classwallet-file.model';
import { CwFileUploadHistoryCreateDto } from '../../models/cw-file-upload-history-create-dto.model';
import { LotteryStudent } from '../../models/lottery-student';

@Injectable({
  providedIn: 'root'
})
export class AwardsService {
  private readonly http: HttpClient = inject(HttpClient);

  public generateLottery(lotteryYearId: string, programTypeId: number): Observable<number> {
    return this.http.get<number>(`${environment.adminApiUrl}/awards/generatelottery/${programTypeId}/${lotteryYearId}`);
  }

  public getESASelectedAcademyYear(): number | null {
    const year = this.getWithExpiry('ESA_SelectedAcademicYear');
    return year ? Number.parseFloat(year) : null;
  }

  public getOSSelectedAcademyYear(): number | null {
    const year = this.getWithExpiry('OS_SelectedAcademicYear');
    return year ? Number.parseFloat(year) : null;
  }

  public getProgramSettingsTab(): number | null {
    const s = localStorage.getItem('Program_Settings_Tab');
    return s === null ? null : Number.parseFloat(s);
  }

  private academicYears$: Observable<AcademicYear[]> | null = null;

  public getAcademicYears(): Observable<AcademicYear[]> {
    this.academicYears$ ??= this.http
      .get<AcademicYear[]>(`${environment.adminApiUrl}/awards/academicyears`)
      .pipe(shareReplay(1));
    return this.academicYears$;
  }

  public updateAcademicYear(academicYear: AcademicYear): Observable<AcademicYear> {
    return this.http
      .put<AcademicYear>(`${environment.adminApiUrl}/awards/academicyear/${academicYear.id}`, academicYear)
      .pipe(tap(() => (this.academicYears$ = null)));
  }

  public getLotteryStudentsESA(academicYearId: number): Observable<LotteryStudent[]> {
    return this.http.get<LotteryStudent[]>(`${environment.adminApiUrl}/awards/lotterystudents/1/${academicYearId}`);
  }

  public getLotteryStudentsOS(academicYearId: number): Observable<LotteryStudent[]> {
    return this.http.get<LotteryStudent[]>(`${environment.adminApiUrl}/awards/lotterystudents/2/${academicYearId}`);
  }

  public getEsaPlusLotteryAwardSettings(academicYearId: number): Observable<LotteryAwardSettings> {
    return this.http.get<LotteryAwardSettings>(`${environment.adminApiUrl}/awards/settings/1/${academicYearId}`);
  }

  public getOsLotteryAwardSettings(academicYearId: number): Observable<LotteryAwardSettings> {
    return this.http.get<LotteryAwardSettings>(`${environment.adminApiUrl}/awards/settings/2/${academicYearId}`);
  }

  public setESASelectedAcademyYear(academicYearId: number): void {
    this.setWithExpiry('ESA_SelectedAcademicYear', academicYearId.toString(), 3600000 * 12);
  }

  public setOSSelectedAcademyYear(academicYearId: number): void {
    this.setWithExpiry('OS_SelectedAcademicYear', academicYearId.toString(), 3600000 * 12);
  }

  public setProgramSettingsTab(tabIndex: number): void {
    localStorage.setItem('Program_Settings_Tab', tabIndex?.toString());
  }

  public updateLotteryAwardSettings(lotterySettings: LotteryAwardSettings, programTypeId: number): Observable<LotteryAwardSettings> {
    return this.http.put<LotteryAwardSettings>(`${environment.adminApiUrl}/awards/settings/${programTypeId}`, lotterySettings);
  }

  public getFundingDetails(academicYearId: number, programTypeId: number): Observable<AwardFundingDetails> {
    return this.http.get<AwardFundingDetails>(
      `${environment.adminApiUrl}/awards/fundingDetails/${programTypeId}/${academicYearId}`
    );
  }

  public getDatFilesHistory(): Observable<DatFileSendHistory[]> {
    return this.http.get<DatFileSendHistory[]>(`${environment.adminApiUrl}/awards/datfiles/history`);
  }

  public downloadDatFile(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.adminApiUrl}/awards/datfiles/download/${id}`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  public getClassWalletFiles(): Observable<ClassWalletFile[]> {
    return this.http.get<ClassWalletFile[]>(`${environment.adminApiUrl}/awards/cwfiles/history`);
  }

  public saveCwFiles(files: CwFileUploadHistoryCreateDto[]): Observable<ClassWalletFile[]> {
    return this.http.post<ClassWalletFile[]>(`${environment.adminApiUrl}/awards/cwfiles/upload`, files);
  }

  // The blob container is private, so the file is streamed through the API rather than linked directly.
  public downloadClassWalletFile(id: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.adminApiUrl}/awards/cwfiles/download/${id}`, {
      responseType: 'blob',
      observe: 'response'
    });
  }
  private getWithExpiry(key: string): string | null {
    const itemStr = localStorage.getItem(key);

    if (!itemStr) {
      return null;
    }
    const item = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  }

  private setWithExpiry(key: string, value: string, ttl: number): void {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + ttl
    };
    localStorage.setItem(key, JSON.stringify(item));
  }
}
